import { useQuery } from "@tanstack/react-query";
import { getLeadMapping } from "../mappings";
import { useCallback, useEffect } from "react";
import {
  getJourneyByOldId,
  getLeadReferrals,
} from "@/modules/lead-page/api/lead-page.v2.api";
import { useNavigate } from "react-router-dom";
import { queryCommonFlags } from "@/api/constants";
import { getStagesByJourney } from "@/modules/lead-page/api/lead-page.v2.api";
import { JourneyRelationModes } from "../types";
import { FeatureFlagsKeys } from "@/modules/common/context/feature-flags.constants";
import useFeatureFlags from "@/modules/common/hooks/use-feature-flags.hook";
import { EnvironmentVariables, OrganizationTierTypes } from "@/modules/common/types";

const useLeadPageDataFetching = (
  lead_id: string | undefined,
  redirect: boolean = true,
  mode: JourneyRelationModes = JourneyRelationModes.GET_LEAD,
  hasQRCode?: boolean
) => {
  const navigate = useNavigate();

  const isNanoId = /^\d+-[a-zA-Z0-9]{4}$/.test(lead_id || "");
  const { hasFlag } = useFeatureFlags();
 

  const getJourneyNanoId = useCallback(async () => {
    const { data } = await getJourneyByOldId({ id: lead_id });

    if (redirect) {
      const oldLeadPageFeatureFlag = hasFlag(FeatureFlagsKeys.OLD_LEAD_PAGE) || EnvironmentVariables.COMPANY_NAME.toString() === OrganizationTierTypes.PARTNERS;
      navigate(
        oldLeadPageFeatureFlag ? `/leads/${data.nanoId}` : `/leads-new/${data.nanoId}`
      );
    }
  }, [lead_id, navigate, redirect]);

  useEffect(() => {
    if (!isNanoId && lead_id) {
      getJourneyNanoId();
    }
  }, [lead_id, isNanoId, getJourneyNanoId]);

  const {
    data: leadData,
    error: leadError,
    isError: leadIsError,
    isLoading,
  } = useQuery({
    queryKey: ["getLead", lead_id, mode, hasQRCode],
    enabled: !!lead_id && isNanoId,
    queryFn: () => getLeadMapping({ id: lead_id, mode, hasQRCode }),
    refetchOnWindowFocus: true,
    refetchOnMount: false, // Prevent refetch on mount since data is shared via context
    refetchOnReconnect: true,
    refetchIntervalInBackground: true,
    staleTime: 0,
    refetchInterval: 600000, // 5 minutes
  });

  const { data: leadReferrals, isLoading: isReferralsLoading } = useQuery({
    queryKey: ["lead-referrals", leadData?.data?.lead.uid],
    queryFn: () => getLeadReferrals(leadData?.data?.lead.uid || ""),
    enabled: !!leadData?.data?.lead.uid,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });

  const {
    data: stagesData,
    isLoading: isStagesLoading,
    isFetching: isStagesFetching,
  } = useQuery({
    queryKey: ["getStagesByJourney", lead_id ?? "no-lead"],
    queryFn: () => {
      if (!lead_id) return Promise.resolve({ data: [] });
      return getStagesByJourney(lead_id);
    },
    ...queryCommonFlags,
    enabled: !!lead_id,
    refetchOnWindowFocus: false,
  });

  return {
    leadData,
    stagesData,
    leadReferrals,
    isLoading: isLoading || isReferralsLoading,
    isStagesLoading: isStagesLoading || isStagesFetching,
    leadError,
    leadIsError,
  };
};

export default useLeadPageDataFetching;
